import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Fragment, useLayoutEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { TRoutes } from '../../shared/const'

import PoweredBy from '../PoweredBy'

import styles from './style.module.scss'

/*
 * Rechts-Links im angemeldeten Bereich. Standen frueher als vierzeilige,
 * weisse Liste unten in der Seitenleiste (Navbar) und wogen dort optisch
 * genauso schwer wie die eigentliche Navigation. Jetzt: schmale, fixierte
 * Fusszeile ueber die volle Breite, hell und zurueckhaltend.
 *
 * Die oeffentlichen Seiten (Startseite, Rechtsseiten) haben mit
 * components/PublicFooter eine eigene, dunkle Fusszeile - dort wird diese
 * Leiste bewusst nicht gezeigt (siehe shouldShowAppFooter).
 */

/* Seiten mit eigener Fusszeile bzw. ohne angemeldete Seitenleiste. */
const ROUTES_WITHOUT_APP_FOOTER = [
	TRoutes.MAIN,
	TRoutes.PUBLIC_COURSE,
	TRoutes.IMPRESSUM,
	TRoutes.DATENSCHUTZ,
	TRoutes.NUTZUNGSBEDINGUNGEN,
	TRoutes.BARRIEREFREIHEIT,
	TRoutes.AUTH_CONFIRM,
	// Die offenen Berichtsseiten bringen die dunkle Fusszeile selbst mit.
	TRoutes.PUBLIC_ANNUAL_REPORT,
	TRoutes.PUBLIC_PLATFORM_REPORT,
	TRoutes.PUBLIC_DAVINCILAB
]

/*
 * Auch von App.js gebraucht: dort haengt daran der untere Innenabstand, der
 * den Platz fuer die fixierte Leiste freihaelt.
 */
export function shouldShowAppFooter(isAuth, pathname) {
	return isAuth && !ROUTES_WITHOUT_APP_FOOTER.includes(pathname)
}

function AppFooter(props) {
	const { t } = useTranslation()
	const location = useLocation()
	const footerRef = useRef(null)
	const visible = shouldShowAppFooter(props.isAuth, location.pathname)

	/*
	 * Die tatsaechliche Hoehe der Leiste als CSS-Variable melden. Auf schmalen
	 * Bildschirmen bricht sie auf zwei oder drei Zeilen um; ein fester Wert im
	 * Stylesheet wuerde dort zu wenig Platz freihalten und die letzte Zeile
	 * langer Listen verschwinden lassen.
	 */
	useLayoutEffect(() => {
		const element = footerRef.current

		if (!element) {
			document.documentElement.style.removeProperty('--app-footer-height')

			return undefined
		}

		const reportHeight = () =>
			document.documentElement.style.setProperty('--app-footer-height', `${element.offsetHeight}px`)

		reportHeight()

		const observer = new ResizeObserver(reportHeight)
		observer.observe(element)

		return () => {
			observer.disconnect()
			document.documentElement.style.removeProperty('--app-footer-height')
		}
	}, [visible])

	if (!visible) {
		return null
	}

	const links = [
		{ to: TRoutes.IMPRESSUM, label: t('Imprint') },
		{ to: TRoutes.DATENSCHUTZ, label: t('Privacy policy') },
		{ to: TRoutes.NUTZUNGSBEDINGUNGEN, label: t('Terms of use') },
		{ to: TRoutes.BARRIEREFREIHEIT, label: t('Accessibility') }
	]

	return (
		<footer className={styles.root} ref={footerRef}>
			<nav className={styles.links} aria-label={t('Legal information')}>
				{links.map((link, index) => (
					<Fragment key={link.to}>
						{index > 0 && (
							<span className={styles.separator} aria-hidden="true">
								·
							</span>
						)}
						<Link className={styles.link} to={link.to}>
							{link.label}
						</Link>
					</Fragment>
				))}
			</nav>
			<PoweredBy className={styles.powered} />
		</footer>
	)
}

AppFooter.propTypes = {
	isAuth: PropTypes.bool.isRequired
}

const mapStateToProps = state => ({
	isAuth: state.user.isAuth
})

export default connect(mapStateToProps)(AppFooter)
